import {initializeApp} from "firebase/app";
import Api from "../api/api";
import {deleteToken, getMessaging, getToken, isSupported, MessagePayload, Messaging, NextFn, Observer, onMessage, Unsubscribe,} from "firebase/messaging";
import AccessLevelsService from "../access-levels";
import CacheService from "../cache/cache_service";
import EnvService from "../env";
import ToastService from "../toast";
import DateUtils from "../date-utils";

/**
 * The interface for handling firebase messaging operations.
 */
class FirebaseNotificationService {
    static _instance: Messaging | null = null;
    static _isSupported: boolean | null = null;
    private static deactivating: boolean = false;

    /**
     * Retrieve an instance of Firebase Messaging so that it can handle background messages.
     */
    static async getInstance(): Promise<Messaging | null> {
        if (FirebaseNotificationService._isSupported == null) {
            FirebaseNotificationService._isSupported = await isSupported();
        }
        if (!FirebaseNotificationService._isSupported) return null;
        if (FirebaseNotificationService._instance == null) {
            FirebaseNotificationService._instance = getMessaging(initializeApp({
                apiKey: "AIzaSyADCXefJozMZ-aBMPbzRSg7SGWRs3BbFU0",
                authDomain: "partner-1688b.firebaseapp.com",
                projectId: "partner-1688b",
                storageBucket: "partner-1688b.appspot.com",
                messagingSenderId: "631930930979",
                appId: "1:631930930979:web:c71e558e11969bf4a02e96",
                measurementId: "G-GPLRBNFT8T",
            }));
        }
        return FirebaseNotificationService._instance;
    }

    /**
     * Subscribes the Messaging instance to push notifications. Returns an Firebase Cloud Messaging registration
     * token that can be used to send push messages to that Messaging instance.
     *
     * If a notification permission isn't already granted, this method asks the user for permission. The returned
     * promise rejects if the user does not allow the app to show notifications.
     *
     * @return {Promise<string>} the token of the firebase messaging used for activation. If this token is null, it
     * might be due to the client not having permissions.
     */
    static async activateDevice(): Promise<string | null> {
        if (!AccessLevelsService.packages.notifications.viewList) {
            return null;
        }
        const instance = await FirebaseNotificationService.getInstance();
        if (!instance) return null;
        const options = {vapidKey: EnvService.NotificationVAPIDKey}
        try {
            const token = await getToken(instance, options)
            if (token) {
                const forApi = {
                    token,
                    expireDatetime: DateUtils.serverNow().add(CacheService.retrieveTokenAndExpiration()?.tokenExpiration ?? 0, "minutes").format(DateUtils.withoutOffsetFormat),
                };
                await Api.submitFirebaseDevice(forApi, () => false)
            }
            // set or remove the notification token based on the state of the received token.
            CacheService.setNotificationToken(token);
            return token;
        } catch (e) {
            // the error may arise in case of blocked notification permission
            ToastService.error({
                title: "Permission Denied",
                message: "To receive notifications from the application, please grant notification permission.",
                props: {
                    position: 'top-right',
                }
            });
            return null;
        }
    }

    /**
     * When a push message is received and the user is currently on a page for your origin, the message is passed to
     * the page and an onMessage() event is dispatched with the payload of the push message.
     *
     * Message payload that contains the notification payload that is represented with
     * firebase.messaging.NotificationPayload and the data payload that contains an arbitrary number of key-value
     * pairs sent by developers through the Send API
     *
     * @param {(MessagePayload) => Promise<void>} listener the listener function that will receive the MessagePayloads
     * @return {() => void} the method used for ub-subscribing the listener.
     */
    static async onMessageReceived(listener: NextFn<MessagePayload> | Observer<MessagePayload>): Promise<Unsubscribe | null> {
        const instance = await FirebaseNotificationService.getInstance();
        if (!instance) return null;
        return onMessage(instance, listener);
    }

    /**
     * Deletes the registration token associated with this Messaging instance and unsubscribes the Messaging instance
     * from the push subscription.
     *
     * @return {Promise<boolean>} a boolean to indicate whether the token as been successfully removed. If the token
     * did not exist, then true is returned and no api call is made.
     */
    static async deactivateDevice(loginRequired = false): Promise<boolean> {
        const canViewNotifications = AccessLevelsService.packages.notifications.viewList;
        const token = CacheService.notificationToken;
        if (!canViewNotifications && !token) {
            return false;
        }
        const instance = await FirebaseNotificationService.getInstance();
        if (!instance) return false;
        if (token === null) {
            return false;
        }
        if (this.deactivating) return false;
        this.deactivating = true
        try {
            const res = await deleteToken(instance)
            if (!res) {
                return res
            }
            CacheService.setNotificationToken(undefined);
            const forApi = {
                token: token
            }
            const response = await Api.deactivateFirebaseDevice(forApi, loginRequired, () => false);
            this.deactivating = false
            return response?.resultFlag ?? false
        } catch (e) {
            this.deactivating = false
            return false
        }
    };

    /**
     * Determines if the browser support push notification.
     *
     * Note: We have to use this method before using the global Notification variable since Safari in IOS does not
     * support it.
     *
     * @return {boolean} a boolean to indicate if this browser supports the notification service.
     */
    static isSupported(): boolean {
        return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window
    }

}

export default FirebaseNotificationService
